<template>
  <!-- Navigation Drawer Version -->
  <v-navigation-drawer
    v-if="useDrawer"
    v-model="isOpen"
    temporary
    right
    width="400"
    class="widget-library"
    overlay-opacity="0.5"
    :overlay="true"
    app
  >
    <v-toolbar flat color="primary" dark>
      <v-toolbar-title class="text-h6">Widget Library</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="$emit('close')">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <v-divider></v-divider>

    <!-- Search -->
    <v-container>
      <v-text-field
        v-model="searchQuery"
        placeholder="Search widgets..."
        prepend-inner-icon="mdi-magnify"
        outlined
        dense
        clearable
        hide-details
      ></v-text-field>
    </v-container>

    <!-- Category Filters -->
    <v-container class="py-2">
      <v-chip-group
        v-model="selectedCategory"
        active-class="primary--text"
        mandatory
      >
        <v-chip
          v-for="(category, index) in categories"
          :key="category.value"
          :value="index"
          small
          outlined
        >
          <v-icon left small>{{ category.icon }}</v-icon>
          {{ category.label }}
        </v-chip>
      </v-chip-group>
    </v-container>

    <v-divider></v-divider>

    <!-- Widget Templates -->
    <v-list class="widget-list">
      <v-list-item
        v-for="template in filteredTemplates"
        :key="template.id"
        @click="addWidget(template)"
        class="widget-item"
      >
        <v-list-item-avatar>
          <v-icon :color="getWidgetColor(template.type)">
            {{ getWidgetIcon(template.type) }}
          </v-icon>
        </v-list-item-avatar>

        <v-list-item-content>
          <v-list-item-title>{{ template.name }}</v-list-item-title>
          <v-list-item-subtitle>{{ template.description }}</v-list-item-subtitle>
          <div class="widget-meta">
            <v-chip x-small outlined>{{ template.category }}</v-chip>
            <span class="text-caption ml-2">
              {{ template.defaultSize.width }}x{{ template.defaultSize.height }}
            </span>
          </div>
        </v-list-item-content>

        <v-list-item-action>
          <v-btn
            icon
            small
            color="primary"
            @click="addWidget(template)"
            :disabled="!canAddWidget"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-list-item-action>
      </v-list-item>
    </v-list>

    <!-- Empty State -->
    <div v-if="filteredTemplates.length === 0" class="empty-state">
      <v-icon size="64" color="grey lighten-2">mdi-widgets-outline</v-icon>
      <p class="text-body-2 mt-4 grey--text">No widgets found</p>
      <p class="text-caption grey--text">Try adjusting your search or category filter</p>
    </div>
  </v-navigation-drawer>

  <!-- Modal Fallback Version -->
  <v-dialog
    v-else
    v-model="isOpen"
    max-width="500"
    persistent
  >
    <v-card>
      <v-card-title class="primary white--text">
        <span class="text-h6">Widget Library</span>
        <v-spacer></v-spacer>
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="pa-0">
        <!-- Widget Templates -->
        <v-list class="widget-list" style="max-height: 400px; overflow-y: auto;">
          <v-list-item
            v-for="template in filteredTemplates"
            :key="template.id"
            @click="addWidget(template)"
            class="widget-item"
          >
            <v-list-item-avatar>
              <v-icon :color="getWidgetColor(template.type)">
                {{ getWidgetIcon(template.type) }}
              </v-icon>
            </v-list-item-avatar>

            <v-list-item-content>
              <v-list-item-title>{{ template.name }}</v-list-item-title>
              <v-list-item-subtitle>{{ template.description }}</v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-btn
                icon
                small
                color="primary"
                @click="addWidget(template)"
                :disabled="!canAddWidget"
              >
                <v-icon>mdi-plus</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>

        <v-divider class="my-4"></v-divider>

        <h3 class="text-h6 mb-3">Categories</h3>
        <v-chip-group
          v-model="selectedCategory"
          column
          mandatory
        >
          <v-chip
            v-for="category in categories"
            :key="category"
            :value="category"
            small
            outlined
          >
            {{ category }}
          </v-chip>
        </v-chip-group>

        <v-divider class="my-4"></v-divider>

        <div v-if="filteredTemplates.length === 0" class="text-center">
          <v-icon size="48" color="grey lighten-2">mdi-widgets</v-icon>
          <p class="text-body-2 mt-4 grey--text">No widgets found</p>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
export default {
  name: 'WidgetLibrary',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    templates: {
      type: Array,
      default: () => []
    },
    useDrawer: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      searchQuery: '',
      selectedCategory: 0,
      categories: [
        { label: 'All', value: 'all', icon: 'mdi-view-grid' },
        { label: 'Alerts', value: 'alerts', icon: 'mdi-alert' },
        { label: 'Devices', value: 'devices', icon: 'mdi-router-wireless' },
        { label: 'Sensors', value: 'sensors', icon: 'mdi-chip' },
        { label: 'Maintenance', value: 'maintenance', icon: 'mdi-wrench' }
      ]
    }
  },
  computed: {
    isOpen: {
      get() {
        return this.show
      },
      set(value) {
        if (!value) {
          this.$emit('close')
        }
      }
    },
    filteredTemplates() {
      let filtered = this.templates || []
      
      // Filter by search query
      if (this.searchQuery) {
        filtered = filtered.filter(template =>
          template.name.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
          template.description.toLowerCase().includes(this.searchQuery.toLowerCase())
        )
      }
      
      // Filter by category (skip if "All" is selected - index 0)
      if (this.selectedCategory !== null && this.selectedCategory !== 0) {
        const selectedCategoryValue = this.categories[this.selectedCategory].value
        filtered = filtered.filter(template => template.category === selectedCategoryValue)
      }
      
      return filtered
    }
  },
  methods: {
    addWidget(template) {
      // Find a good position for the new widget
      const position = this.findAvailablePosition(template.defaultSize)
      
      this.$emit('add-widget', {
        templateId: template.id,
        position
      })
    },
    findAvailablePosition(size) {
      const currentDashboard = this.$store.getters.currentDashboard
      const currentWidgets = currentDashboard && currentDashboard.widgets ? currentDashboard.widgets : []
      
      for (let y = 0; y < 20; y++) {
        for (let x = 0; x <= 12 - size.width; x++) {
          const newPosition = {
            x,
            y,
            width: size.width,
            height: size.height
          }
          
          const hasCollision = currentWidgets.some(widget => {
            const w = widget.position
            return !(newPosition.x + newPosition.width <= w.x || 
                    w.x + w.width <= newPosition.x ||
                    newPosition.y + newPosition.height <= w.y ||
                    w.y + w.height <= newPosition.y)
          })
          
          if (!hasCollision) {
            return newPosition
          }
        }
      }
      
      const maxY = currentWidgets.length > 0 ? Math.max(0, ...currentWidgets.map(w => w.position.y + w.position.height)) : 0
      return {
        x: 0,
        y: maxY,
        width: size.width,
        height: size.height
      }
    },
    getWidgetIcon(type) {
      const iconMap = {
        'metric': 'mdi-chart-line',
        'chart': 'mdi-chart-bar',
        'table': 'mdi-table',
        'kpi': 'mdi-speedometer'
      }
      return iconMap[type] || 'mdi-widgets'
    },
    getWidgetColor(type) {
      const colorMap = {
        'metric': '#2196F3',
        'chart': '#4CAF50',
        'table': '#FF9800',
        'kpi': '#9C27B0'
      }
      return colorMap[type] || '#757575'
    }
  }
}
</script>

<style scoped>
.widget-library {
  z-index: 1000;
}

.widget-list {
  max-height: calc(100vh - 200px);
  overflow-y: auto;
}

.widget-item {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s ease;
}

.widget-item:hover {
  background-color: rgba(33, 150, 243, 0.04);
}

.widget-meta {
  display: flex;
  align-items: center;
  margin-top: 4px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  text-align: center;
  height: 300px;
}

/* Custom scrollbar */
.widget-list::-webkit-scrollbar {
  width: 6px;
}

.widget-list::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.widget-list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.widget-list::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

@media (max-width: 600px) {
  .widget-library {
    width: 100% !important;
  }
}
</style> 